import * as React from 'react';

import { ReactFCC } from '@notino/react-styleguide';

import { CART_URL } from '@utils/constants';
import { noop } from '@utils/noop';

import { IAddToCartButtonProps } from '../model';

import { IAddToCartButtonState, IAddToCartButtonContext } from './model';
import { defaultState, reducer } from './reducer';
import { useActions } from './useActions';
import { useAddToCart } from './useAddToCart';

const AddToCartButtonContext = React.createContext<IAddToCartButtonContext>(
  {} as IAddToCartButtonContext
);

export const AddToCartButtonProvider: ReactFCC<IAddToCartButtonProps> = ({
  children,
  product,
  count = 1,
  buttonWrapper,
  buttonWrapperProps,
  buttonElementId,
  buttonElementClassName,
  wrapable,
  buttonStyle,
  buttonSize,
  buttonText,
  onProductAdded = noop,
  onProductAddFailed = noop,
  handleGtmPush = noop,
  trackAddToCartAsync,
  onClosingModal = noop,
  disableRedirect = false,
  showAddToCartModal,
  services,
  withLegacyAddToCart = false,
  getModalAfterAddToCart,
}) => {
  const initialState: IAddToCartButtonState = {
    ...defaultState,
    product,
    buttonWrapper,
    buttonWrapperProps,
    buttonElementId,
    buttonElementClassName,
    wrapable,
    buttonStyle,
    buttonSize,
    buttonText,
    onProductAdded,
    onProductAddFailed,
    handleGtmPush,
    cartUrl: CART_URL,
    onClosingModal,
    disableRedirect,
    showAddToCartModal,
    services,
    count,
    trackAddToCartAsync,
    withLegacyAddToCart,
  };
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const actions = useActions(dispatch, { getModalAfterAddToCart });

  const apollo = useAddToCart();

  const { updateStateAfterPropsChange } = actions;

  React.useEffect(() => {
    updateStateAfterPropsChange({
      product,
      count,
      buttonWrapper,
      buttonWrapperProps,
      buttonElementId,
      buttonElementClassName,
      wrapable,
      buttonStyle,
      buttonSize,
      buttonText,
      onProductAdded,
      handleGtmPush,
      onClosingModal,
      disableRedirect,
      showAddToCartModal,
      services,
      trackAddToCartAsync,
      withLegacyAddToCart,
    });
  }, [
    product,
    count,
    buttonWrapper,
    buttonWrapperProps,
    buttonElementId,
    buttonElementClassName,
    wrapable,
    buttonStyle,
    buttonSize,
    buttonText,
    onProductAdded,
    handleGtmPush,
    onClosingModal,
    disableRedirect,
    showAddToCartModal,
    services,
    trackAddToCartAsync,
    updateStateAfterPropsChange,
    withLegacyAddToCart,
  ]);

  if (!product) {
    return null;
  }

  return (
    <AddToCartButtonContext.Provider value={{ state, actions, apollo }}>
      {children}
    </AddToCartButtonContext.Provider>
  );
};

export const useAddToCartButtonContext = (): IAddToCartButtonContext =>
  React.useContext(AddToCartButtonContext);
